import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const services = [
  {
    id: 'strategy',
    label: 'Strategy',
    title: 'Konumlama & Kanal Çerçevesi',
    description: 'Markanızın nerede durduğunu, nereye gittiğini ve hangi mecralarda ses getireceğini netleştiriyoruz. Strateji olmadan içerik gürültü, kitleyle temas şans işidir.',
    bullets: ['Pazar konumlaması', 'Kanal seçimi & önceliklendirme', 'Mesaj mimarisi'],
    color: 'bg-gradient-to-br from-slate-900 via-slate-800 to-zinc-900',
    textColor: 'text-white',
    labelColor: 'text-amber-400',
    descColor: 'text-slate-300',
    bulletColor: 'text-slate-400',
    link: '/strategy',
    large: true,
  },
  {
    id: 'social',
    label: 'Social Media',
    title: 'İçerik Sistemi & Topluluk',
    description: 'Sosyal medyayı sistematik yönetiyoruz. Sonuçları ölçülen, tutarlı ve estetik bir içerik makinesi kuruyoruz.',
    color: 'bg-gradient-to-br from-neutral-100 to-stone-100',
    textColor: 'text-charcoal',
    labelColor: 'text-stone-500',
    descColor: 'text-gray-600',
    link: '/social-media',
    large: false,
  },
  {
    id: 'influencer',
    label: 'Influencer & UGC',
    title: 'Seçim, Anlaşma & Ölçüm',
    description: 'Marka değerinize uygun isimleri seçiyor, anlaşıyor ve performansı gerçek veriyle takip ediyoruz. Yanlış influencer marka değeri yitirir — biz bunu en baştan doğru kuruyoruz.',
    color: 'bg-gradient-to-br from-amber-50 to-orange-50',
    textColor: 'text-charcoal',
    labelColor: 'text-amber-700',
    descColor: 'text-gray-600',
    link: '#',
    large: false,
  },
  {
    id: 'ai',
    label: 'AI Studio',
    title: 'Hızlı Konsept & Varyasyon',
    description: 'Yapay zeka ile içerik üretimini haftalardan günlere indiriyoruz. Konsept, görsel, kopya — hızlı, tutarlı ve ölçeklenebilir.',
    color: 'bg-gradient-to-br from-zinc-800 to-neutral-900',
    textColor: 'text-white',
    labelColor: 'text-amber-400',
    descColor: 'text-zinc-300',
    link: '#',
    large: false,
  },
  {
    id: 'experience',
    label: 'ExperIence',
    title: 'Lansman & PR Anları',
    description: 'Bir lansman, bir etkinlik, bir PR anı — markayı konuşturmak için doğru anı, doğru ortamda, kalıcı bir iz bırakacak şekilde tasarlıyoruz.',
    color: 'bg-gradient-to-br from-stone-200 to-neutral-200',
    textColor: 'text-charcoal',
    labelColor: 'text-stone-600',
    descColor: 'text-gray-600',
    link: '#',
    large: false,
  },
];

export default function Services() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="services" ref={ref} className="py-16 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-10 md:mb-20 leading-tight"
        >
          Neler
          <br />
          Yapıyoruz
        </motion.h2>

        {/* Row 1: Strategy (2 cols) + Social Media (1 col) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          {/* Strategy - Large */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-2"
          >
            <Link
              to={services[0].link}
              className={`block ${services[0].color} rounded-3xl p-7 md:p-10 h-full min-h-[280px] md:min-h-[320px] hover:-translate-y-1 transition-all duration-500 cursor-pointer group overflow-hidden relative`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 h-full flex flex-col">
                <span className={`font-sans text-xs uppercase ${services[0].labelColor} tracking-[0.25em] font-semibold`}>
                  {services[0].label}
                </span>
                <h3 className={`font-serif text-3xl md:text-4xl ${services[0].textColor} mt-4 mb-3 leading-tight`}>
                  {services[0].title}
                </h3>
                <p className={`font-sans text-sm md:text-base ${services[0].descColor} leading-relaxed mb-5 max-w-lg`}>
                  {services[0].description}
                </p>
                <div className="flex flex-wrap gap-4 mt-auto">
                  {services[0].bullets?.map((b) => (
                    <span key={b} className={`font-sans text-xs ${services[0].bulletColor} tracking-wide flex items-center gap-1.5`}>
                      <span className="w-1 h-1 rounded-full bg-amber-400 inline-block" />
                      {b}
                    </span>
                  ))}
                </div>
                <div className="absolute bottom-7 right-7 md:bottom-10 md:right-10">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-white/15 rounded-full group-hover:scale-110 group-hover:bg-white/25 transition-all duration-300">
                    <i className="ri-arrow-right-line text-white text-base md:text-lg" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Social Media */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link
              to={services[1].link}
              className={`block ${services[1].color} rounded-3xl p-7 md:p-8 h-full min-h-[240px] md:min-h-[320px] hover:-translate-y-1 transition-all duration-500 cursor-pointer group overflow-hidden relative`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 flex flex-col h-full">
                <span className={`font-sans text-xs uppercase ${services[1].labelColor} tracking-[0.25em] font-semibold`}>
                  {services[1].label}
                </span>
                <h3 className={`font-serif text-2xl md:text-3xl ${services[1].textColor} mt-4 mb-3 leading-tight`}>
                  {services[1].title}
                </h3>
                <p className={`font-sans text-sm ${services[1].descColor} leading-relaxed flex-grow`}>
                  {services[1].description}
                </p>
                <div className="flex justify-end mt-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-charcoal/10 rounded-full group-hover:scale-110 group-hover:bg-charcoal/15 transition-all duration-300">
                    <i className="ri-arrow-right-line text-charcoal text-base" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>

        {/* Row 2: Influencer + AI Studio + Experience (3 equal cols) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {services.slice(2).map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <Link
                to={service.link}
                className={`block ${service.color} rounded-3xl p-7 md:p-8 h-full min-h-[240px] md:min-h-[280px] hover:-translate-y-1 transition-all duration-500 cursor-pointer group overflow-hidden relative`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 flex flex-col h-full">
                  <span className={`font-sans text-xs uppercase ${service.labelColor} tracking-[0.25em] font-semibold`}>
                    {service.label}
                  </span>
                  <h3 className={`font-serif text-2xl ${service.textColor} mt-4 mb-3 leading-tight`}>
                    {service.title}
                  </h3>
                  <p className={`font-sans text-sm ${service.descColor} leading-relaxed flex-grow`}>
                    {service.description}
                  </p>
                  <div className="flex justify-end mt-5">
                    <div className={`w-10 h-10 flex items-center justify-center ${
                      service.id === 'ai' ? 'bg-white/15' : 'bg-charcoal/10'
                    } rounded-full group-hover:scale-110 transition-all duration-300`}>
                      <i className={`ri-arrow-right-line ${service.textColor} text-base`} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
